/**
 * "Bir işlem devam ediyor", wherever in the project you are.
 *
 * A reconstruction takes minutes to hours, and the screen that started it is
 * the one screen somebody does not sit on while it runs - they go back to
 * Çekimler, or to Kapsama, or they leave and come back tomorrow. Until now the
 * only place the running job appeared was the tab that started it and the
 * delete dialog, so leaving that tab looked exactly like nothing running.
 *
 * ## What it is not
 *
 * Not a notification centre. That already exists in the bell, and it answers a
 * different question - what *happened* - which is why it is the wrong place
 * for this one: work in flight is not news, it is a state, and a state belongs
 * beside the thing it is a state of.
 *
 * ## Why it polls, and why slowly
 *
 * There is no push channel for job state. Thirty seconds is chosen against
 * what the strip actually says: a phase name that changes every few minutes.
 * Polling faster would not tell anyone anything sooner and would put every
 * open tab on the API. It stops while the tab is hidden, because a background
 * tab has no reader.
 *
 * A failed poll shows nothing rather than an error. The strip is a courtesy
 * beside the project, and the job is unaffected by whether this line drew.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { getProjectActiveWork } from "@/api/client";
import type { ActiveWork } from "@/api/types";
import { paths } from "@/app/paths";

const POLL_MS = 30_000;

/** Turkish for the phase, when the server names one this build knows. */
const PHASE_WORDS: Record<string, string> = {
  sparse: "kamera konumları çıkarılıyor",
  dense: "yoğun nokta bulutu oluşturuluyor",
  mesh: "yüzey modeli oluşturuluyor",
  refine: "yüzey iyileştiriliyor",
  texture: "doku uygulanıyor",
  package: "dosyalar hazırlanıyor",
};

function sentence(items: ActiveWork[]): string {
  if (items.length > 1) return `${items.length} işlem devam ediyor`;
  const phase = items[0]?.phase;
  const words = phase ? PHASE_WORDS[phase] : undefined;
  // An unknown phase name is never shown raw: "mesh_refine_v2" is a server
  // word, and the honest fallback is the thing the customer asked for.
  return words ? `3D çalışması sürüyor — ${words}` : "3D çalışması devam ediyor";
}

export function ActiveWorkStrip({ projectId }: { projectId: string }) {
  const [items, setItems] = useState<ActiveWork[]>([]);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      if (document.hidden) {
        timer = setTimeout(() => void poll(), POLL_MS);
        return;
      }
      try {
        const rows = await getProjectActiveWork(projectId);
        if (!cancelled) setItems(rows);
      } catch {
        if (!cancelled) setItems([]);
      }
      if (!cancelled) timer = setTimeout(() => void poll(), POLL_MS);
    };
    void poll();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [projectId]);

  if (items.length === 0) return null;

  return (
    <div className="card row" role="status" style={{ justifyContent: "space-between", gap: "1rem" }}>
      <span>{sentence(items)}</span>
      <Link to={paths.projectReconstruction(projectId)}>Duruma bak</Link>
    </div>
  );
}
